import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  Table,
  Button,
  Modal,
  message,
  Dropdown,
  Menu,
  Input,
  InputNumber,
  Collapse,
  Select,
  Tag,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import {
  deleteSeason,
  deleteCommercialDate,
  getCommercialDates,
  getCommercialDateEditionsForYear,
  getSeasons,
  getOrganizationParameters,
  createOrUpdateOrganizationParameter,
  getAllCategories,
} from "../../api/Services";
import { Category } from "../../models/Models";

import RewardsTicketBucketsEditor from "./RewardsTicketBucketsEditor";
import styles from "./Parameters.module.scss";

type OrganizationParameterDefinition = {
  key: string;
  default: string;
  descriptionKey: string;
};

const ACTIVATION_PARAM_KEYS: readonly OrganizationParameterDefinition[] = [
  {
    key: "ACTIVATION_MIN_AUDIENCE",
    default: "50",
    descriptionKey: "ActivationParam-MinAudience",
  },
  {
    key: "ACTIVATION_CAMPAIGN_EXCLUSION_DAYS",
    default: "30",
    descriptionKey: "ActivationParam-CampaignExclusionDays",
  },
  {
    key: "ACTIVATION_MIN_STOCK_NOVELTY",
    default: "10",
    descriptionKey: "ActivationParam-MinStockNovelty",
  },
  {
    key: "ACTIVATION_NEW_ARTICLE_DAYS",
    default: "30",
    descriptionKey: "ActivationParam-NewArticleDays",
  },
  {
    key: "ACTIVATION_MIN_SHARED_ATTRIBUTES",
    default: "2",
    descriptionKey: "ActivationParam-MinSharedAttributes",
  },
  {
    key: "ACTIVATION_RENEWAL_MONTHS",
    default: "12",
    descriptionKey: "ActivationParam-RenewalMonths",
  },
  {
    key: "ACTIVATION_MAX_RECOMMENDED_ARTICLES",
    default: "4",
    descriptionKey: "ActivationParam-MaxRecommendedArticles",
  },
] as const;

const SEGMENT_ENGINE_PARAM_KEYS: readonly OrganizationParameterDefinition[] = [
  {
    key: "INACTIVITY_DAYS",
    default: "90",
    descriptionKey: "SegmentEngineParam-InactivityDays",
  },
  {
    key: "INACTIVITY_DAYS_MID",
    default: "180",
    descriptionKey: "SegmentEngineParam-InactivityDaysMid",
  },
  {
    key: "INACTIVITY_DAYS_HIGH",
    default: "365",
    descriptionKey: "SegmentEngineParam-InactivityDaysHigh",
  },
  {
    key: "FREQUENCY_DROP_THRESHOLD",
    default: "1.5",
    descriptionKey: "SegmentEngineParam-FrequencyDropThreshold",
  },
  {
    key: "HIGH_VALUE_PERCENTILE",
    default: "0.8",
    descriptionKey: "SegmentEngineParam-HighValuePercentile",
  },
  {
    key: "LOW_TICKET_PERCENTILE",
    default: "0.3",
    descriptionKey: "SegmentEngineParam-LowTicketPercentile",
  },
  {
    key: "AFFINITY_THRESHOLD",
    default: "0.6",
    descriptionKey: "SegmentEngineParam-AffinityThreshold",
  },
  {
    key: "MIN_SEGMENT_SIZE",
    default: "50",
    descriptionKey: "SegmentEngineParam-MinSegmentSize",
  },
  {
    key: "MIN_SEGMENT_CONTACTABLE_CLIENTS",
    default: "0",
    descriptionKey: "SegmentEngineParam-MinSegmentContactableClients",
  },
] as const;

const REWARDS_ENGINE_PARAM_KEYS: readonly OrganizationParameterDefinition[] = [
  {
    key: "REWARDS_TICKET_LOOKBACK_DAYS",
    default: "365",
    descriptionKey: "RewardsEngineParam-TicketLookbackDays",
  },
];

const COMMERCIAL_OPPORTUNITY_PARAM_KEYS: readonly OrganizationParameterDefinition[] =
  [
    {
      key: "LOW_PERFORMANCE_THRESHOLD",
      default: "-10",
      descriptionKey: "CommercialOpportunityParam-LowPerformanceThreshold",
    },
    {
      key: "CRITICAL_PERFORMANCE_THRESHOLD",
      default: "-25",
      descriptionKey: "CommercialOpportunityParam-CriticalPerformanceThreshold",
    },
    {
      key: "PERFORMANCE_REFERENCE_DAYS",
      default: "90",
      descriptionKey: "CommercialOpportunityParam-PerformanceReferenceDays",
    },
    {
      key: "STORE_REACTIVATION_MIN_DAYS",
      default: "30",
      descriptionKey: "CommercialOpportunityParam-StoreReactivationMinDays",
    },
    {
      key: "MIN_STOCK_EXCESS_UNITS",
      default: "100",
      descriptionKey: "CommercialOpportunityParam-MinStockExcessUnits",
    },
    {
      key: "MAX_STOCK_EXCESS_SEGMENTS",
      default: "15",
      descriptionKey: "CommercialOpportunityParam-MaxStockExcessSegments",
    },
    {
      key: "STOCK_EXCESS_WEEKS",
      default: "12",
      descriptionKey: "CommercialOpportunityParam-StockExcessWeeks",
    },
    {
      key: "STOCK_SALES_WINDOW_DAYS",
      default: "90",
      descriptionKey: "CommercialOpportunityParam-StockSalesWindowDays",
    },
    {
      key: "CHILD_RENEWAL_MONTHS",
      default: "6",
      descriptionKey: "CommercialOpportunityParam-ChildRenewalMonths",
    },
    {
      key: "CHILD_CATEGORY_IDS",
      default: "",
      descriptionKey: "CommercialOpportunityParam-ChildCategoryIds",
    },
    {
      key: "POINTS_EXPIRATION_WARNING_DAYS",
      default: "30",
      descriptionKey: "CommercialOpportunityParam-PointsExpirationWarningDays",
    },
    {
      key: "MIN_POINTS_TO_REDEEM",
      default: "100",
      descriptionKey: "CommercialOpportunityParam-MinPointsToRedeem",
    },
    {
      key: "HIGH_POINTS_THRESHOLD",
      default: "1000",
      descriptionKey: "CommercialOpportunityParam-HighPointsThreshold",
    },
    {
      key: "LOW_POINTS_MARGIN",
      default: "100",
      descriptionKey: "CommercialOpportunityParam-LowPointsMargin",
    },
  ] as const;

const PRODUCT_MIX_PARAM_KEYS: readonly OrganizationParameterDefinition[] = [
  {
    key: "cotizacion_usd",
    default: "40",
    descriptionKey: "ProductMixParam-UsdFallback",
  },
  {
    key: "PRODUCT_MIX_INSIGHT_CATEGORY_SHARE_MIN",
    default: "25",
    descriptionKey: "ProductMixParam-InsightCategoryShareMin",
  },
  {
    key: "PRODUCT_MIX_INSIGHT_BRAND_SHARE_MIN",
    default: "20",
    descriptionKey: "ProductMixParam-InsightBrandShareMin",
  },
  {
    key: "PRODUCT_MIX_INSIGHT_PRICE_RANGE_SHARE_MIN",
    default: "25",
    descriptionKey: "ProductMixParam-InsightPriceRangeShareMin",
  },
  {
    key: "PRODUCT_MIX_INSIGHT_TOP10_SHARE_MIN",
    default: "35",
    descriptionKey: "ProductMixParam-InsightTop10ShareMin",
  },
  {
    key: "PRODUCT_MIX_INSIGHT_MIX_SHIFT_PP",
    default: "1",
    descriptionKey: "ProductMixParam-InsightMixShiftPp",
  },
  {
    key: "PRODUCT_MIX_ALERT_TOP10_SHARE_MIN",
    default: "40",
    descriptionKey: "ProductMixParam-AlertTop10ShareMin",
  },
  {
    key: "PRODUCT_MIX_ALERT_TOP10_SHARE_LOW",
    default: "35",
    descriptionKey: "ProductMixParam-AlertTop10ShareLow",
  },
  {
    key: "PRODUCT_MIX_ALERT_TOP10_SHARE_HIGH",
    default: "50",
    descriptionKey: "ProductMixParam-AlertTop10ShareHigh",
  },
  {
    key: "PRODUCT_MIX_ALERT_BRAND_SHARE_MIN",
    default: "25",
    descriptionKey: "ProductMixParam-AlertBrandShareMin",
  },
  {
    key: "PRODUCT_MIX_ALERT_CATALOG_ACTIVE_MAX",
    default: "70",
    descriptionKey: "ProductMixParam-AlertCatalogActiveMax",
  },
  {
    key: "PRODUCT_MIX_ALERT_MIX_DROP_PP",
    default: "-1",
    descriptionKey: "ProductMixParam-AlertMixDropPp",
  },
  {
    key: "PRODUCT_MIX_ALERT_CATEGORY_MIN_WEIGHT",
    default: "5",
    descriptionKey: "ProductMixParam-AlertCategoryMinWeight",
  },
  {
    key: "PRODUCT_MIX_ALERT_PROMOTED_SALES_MIN",
    default: "55",
    descriptionKey: "ProductMixParam-AlertPromotedSalesMin",
  },
  {
    key: "PRODUCT_MIX_ALERT_PRICE_RANGE_SHARE_MIN",
    default: "40",
    descriptionKey: "ProductMixParam-AlertPriceRangeShareMin",
  },
  {
    key: "PRODUCT_MIX_ALERT_BRAND_DIVERSITY_RATIO",
    default: "0.85",
    descriptionKey: "ProductMixParam-AlertBrandDiversityRatio",
  },
  {
    key: "PRODUCT_MIX_PRICE_BUCKET_1",
    default: "1000",
    descriptionKey: "ProductMixParam-PriceBucket1",
  },
  {
    key: "PRODUCT_MIX_PRICE_BUCKET_2",
    default: "2500",
    descriptionKey: "ProductMixParam-PriceBucket2",
  },
  {
    key: "PRODUCT_MIX_PRICE_BUCKET_3",
    default: "4000",
    descriptionKey: "ProductMixParam-PriceBucket3",
  },
  {
    key: "PRODUCT_MIX_PRICE_BUCKET_4",
    default: "6000",
    descriptionKey: "ProductMixParam-PriceBucket4",
  },
] as const;

const DAILY_CONTROL_PARAM_KEYS: readonly OrganizationParameterDefinition[] = [
  {
    key: "DAILY_CONTROL_GROWTH_TARGET_PCT",
    default: "5",
    descriptionKey: "DailyControlParam-GrowthTargetPct",
  },
] as const;

const PRODUCT_ANALYSIS_PARAM_KEYS: readonly OrganizationParameterDefinition[] = [
  {
    key: "PRODUCT_ANALYSIS_ALERT_HIGH_STOCK_UNITS",
    default: "50",
    descriptionKey: "ProductAnalysisParam-HighStockUnits",
  },
  {
    key: "PRODUCT_ANALYSIS_ALERT_HIGH_STOCK_DAYS",
    default: "60",
    descriptionKey: "ProductAnalysisParam-HighStockDays",
  },
  {
    key: "PRODUCT_ANALYSIS_OPPORTUNITY_LOW_STOCK_DAYS",
    default: "15",
    descriptionKey: "ProductAnalysisParam-LowStockDays",
  },
] as const;

const PROMOTION_PARAM_KEYS: readonly OrganizationParameterDefinition[] = [
  {
    key: "REUSE_PROMOTIONS",
    default: "false",
    descriptionKey: "PromotionParam-ReusePromotions",
  },
  {
    key: "PROMOTION_DISCOUNT_VALUES",
    default: "10,20,30",
    descriptionKey: "PromotionParam-DiscountValues",
  },
  {
    key: "PROMOTION_DAYS_IN_ADVANCE",
    default: "7",
    descriptionKey: "PromotionParam-DaysInAdvance",
  },
  {
    key: "PROMOTION_MAX_DURATION_DAYS",
    default: "30",
    descriptionKey: "PromotionParam-MaxDurationDays",
  },
] as const;

const Parameters = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [commercialDates, setCommercialDates] = useState<any[]>([]);
  const [currentYearEditionsByDate, setCurrentYearEditionsByDate] = useState<
    Record<number, any>
  >({});
  const currentYear = new Date().getFullYear();
  const [seasons, setSeasons] = useState<any[]>([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [loadingSeasons, setLoadingSeasons] = useState(true);
  const [actualPageDates, setActualPageDates] = useState(1);
  const [actualPageSeasons, setActualPageSeasons] = useState(1);
  const [totalSizeDates, setTotalSizeDates] = useState(0);
  const [totalSizeSeasons, setTotalSizeSeasons] = useState(0);

  const [activationParams, setActivationParams] = useState<
    Record<string, string>
  >({});
  const [segmentEngineParams, setSegmentEngineParams] = useState<
    Record<string, string>
  >({});
  const [commercialOpportunityParams, setCommercialOpportunityParams] =
    useState<Record<string, string>>({});
  const [rewardsEngineParams, setRewardsEngineParams] = useState<
    Record<string, string>
  >({});
  const [productMixParams, setProductMixParams] = useState<
    Record<string, string>
  >({});
  const [dailyControlParams, setDailyControlParams] = useState<
    Record<string, string>
  >({});
  const [productAnalysisParams, setProductAnalysisParams] = useState<
    Record<string, string>
  >({});
  const [promotionParams, setPromotionParams] = useState<
    Record<string, string>
  >({});
  const [loadingActivationParams, setLoadingActivationParams] = useState(true);
  const [loadingSegmentEngineParams, setLoadingSegmentEngineParams] =
    useState(true);
  const [
    loadingCommercialOpportunityParams,
    setLoadingCommercialOpportunityParams,
  ] = useState(true);
  const [loadingRewardsEngineParams, setLoadingRewardsEngineParams] =
    useState(true);
  const [loadingProductMixParams, setLoadingProductMixParams] = useState(true);
  const [loadingDailyControlParams, setLoadingDailyControlParams] =
    useState(true);
  const [loadingProductAnalysisParams, setLoadingProductAnalysisParams] =
    useState(true);
  const [loadingPromotionParams, setLoadingPromotionParams] = useState(true);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingParam, setEditingParam] = useState<{
    key: string;
    value: string;
    descriptionKey: string;
    group:
      | "activation"
      | "segmentEngine"
      | "commercialOpportunities"
      | "rewardsEngine"
      | "productMix"
      | "dailyControl"
      | "productAnalysis"
      | "promotions";
  } | null>(null);
  const [editValue, setEditValue] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeCollapseKeys, setActiveCollapseKeys] = useState<string[]>([
    "commercialDates",
    "seasons",
    "activationParams",
    "segmentEngineParams",
    "commercialOpportunityParams",
    "rewardsEngineParams",
    "productMixParams",
    "dailyControlParams",
    "productAnalysisParams",
    "promotionParams",
  ]);

  useEffect(() => {
    handleFetchCommercialDates(1);
    handleFetchCurrentYearEditions();
    handleFetchSeasons(1);
    handleFetchOrganizationParams();
    handleFetchCategories();
    setActualPageDates(1);
    setActualPageSeasons(1);
  }, []);

  const handleFetchCurrentYearEditions = async () => {
    const editions = await getCommercialDateEditionsForYear(currentYear);
    const byDate: Record<number, any> = {};
    editions.forEach((edition: any) => {
      byDate[edition.commercialDateId] = edition;
    });
    setCurrentYearEditionsByDate(byDate);
  };

  const handleFetchCategories = async () => {
    const response = await getAllCategories();
    setCategories(response);
  };

  const handleFetchCommercialDates = async (pagination = actualPageDates) => {
    setLoadingDates(true);
    const response = await getCommercialDates(pagination - 1);
    setCommercialDates(response.commercialDates);
    setTotalSizeDates(response.totalElements);
    setLoadingDates(false);
  };

  const handleFetchSeasons = async (pagination = actualPageSeasons) => {
    setLoadingSeasons(true);
    const response = await getSeasons(pagination - 1);
    setSeasons(response.seasons);
    setTotalSizeSeasons(response.totalElements);
    setLoadingSeasons(false);
  };

  const handlePaginationSeasons = (page: number) => {
    handleFetchSeasons(page);
    setActualPageSeasons(page);
  };

  const handlePaginationComercialDates = (page: number) => {
    handleFetchCommercialDates(page);
    setActualPageDates(page);
  };

  const handleDeleteSeason = async (id: number) => {
    setLoadingSeasons(true);
    const deleteStatus = await deleteSeason(id);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await handleFetchSeasons();
    setLoadingSeasons(false);
  };

  const handleDeleteCommercialDate = async (id: number) => {
    setLoadingDates(true);
    const deleteStatus = await deleteCommercialDate(id);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await handleFetchCommercialDates();
    setLoadingDates(false);
  };

  const handleFetchOrganizationParams = async () => {
    setLoadingActivationParams(true);
    setLoadingSegmentEngineParams(true);
    setLoadingCommercialOpportunityParams(true);
    setLoadingRewardsEngineParams(true);
    setLoadingProductMixParams(true);
    setLoadingDailyControlParams(true);
    setLoadingProductAnalysisParams(true);
    setLoadingPromotionParams(true);
    const params = await getOrganizationParameters();
    const paramMap: Record<string, string> = {};
    if (Array.isArray(params)) {
      params.forEach((p: { paramKey: string; paramValue: string }) => {
        paramMap[p.paramKey] = p.paramValue;
      });
    }
    const mergedActivation: Record<string, string> = {};
    ACTIVATION_PARAM_KEYS.forEach(({ key, default: def }) => {
      mergedActivation[key] = paramMap[key] ?? def;
    });
    const mergedSegmentEngine: Record<string, string> = {};
    SEGMENT_ENGINE_PARAM_KEYS.forEach(({ key, default: def }) => {
      mergedSegmentEngine[key] = paramMap[key] ?? def;
    });
    const mergedCommercialOpportunity: Record<string, string> = {};
    COMMERCIAL_OPPORTUNITY_PARAM_KEYS.forEach(({ key, default: def }) => {
      mergedCommercialOpportunity[key] = paramMap[key] ?? def;
    });
    const mergedRewardsEngine: Record<string, string> = {};
    REWARDS_ENGINE_PARAM_KEYS.forEach(({ key, default: def }) => {
      mergedRewardsEngine[key] = paramMap[key] ?? def;
    });
    const mergedProductMix: Record<string, string> = {};
    PRODUCT_MIX_PARAM_KEYS.forEach(({ key, default: def }) => {
      mergedProductMix[key] = paramMap[key] ?? def;
    });
    const mergedDailyControl: Record<string, string> = {};
    DAILY_CONTROL_PARAM_KEYS.forEach(({ key, default: def }) => {
      mergedDailyControl[key] = paramMap[key] ?? def;
    });
    const mergedProductAnalysis: Record<string, string> = {};
    PRODUCT_ANALYSIS_PARAM_KEYS.forEach(({ key, default: def }) => {
      mergedProductAnalysis[key] = paramMap[key] ?? def;
    });
    const mergedPromotions: Record<string, string> = {};
    PROMOTION_PARAM_KEYS.forEach(({ key, default: def }) => {
      mergedPromotions[key] = paramMap[key] ?? def;
    });
    setActivationParams(mergedActivation);
    setSegmentEngineParams(mergedSegmentEngine);
    setCommercialOpportunityParams(mergedCommercialOpportunity);
    setRewardsEngineParams(mergedRewardsEngine);
    setProductMixParams(mergedProductMix);
    setDailyControlParams(mergedDailyControl);
    setProductAnalysisParams(mergedProductAnalysis);
    setPromotionParams(mergedPromotions);
    setLoadingActivationParams(false);
    setLoadingSegmentEngineParams(false);
    setLoadingCommercialOpportunityParams(false);
    setLoadingRewardsEngineParams(false);
    setLoadingProductMixParams(false);
    setLoadingDailyControlParams(false);
    setLoadingProductAnalysisParams(false);
    setLoadingPromotionParams(false);
  };

  const handleOpenEditParam = (
    paramKey: string,
    descriptionKey: string,
    group:
      | "activation"
      | "segmentEngine"
      | "commercialOpportunities"
      | "rewardsEngine"
      | "productMix"
      | "dailyControl"
      | "productAnalysis"
      | "promotions"
  ) => {
    const definitions =
      group === "activation"
        ? ACTIVATION_PARAM_KEYS
        : group === "segmentEngine"
          ? SEGMENT_ENGINE_PARAM_KEYS
          : group === "commercialOpportunities"
            ? COMMERCIAL_OPPORTUNITY_PARAM_KEYS
            : group === "rewardsEngine"
              ? REWARDS_ENGINE_PARAM_KEYS
            : group === "productMix"
              ? PRODUCT_MIX_PARAM_KEYS
              : group === "dailyControl"
                ? DAILY_CONTROL_PARAM_KEYS
                : group === "productAnalysis"
                  ? PRODUCT_ANALYSIS_PARAM_KEYS
                  : PROMOTION_PARAM_KEYS;
    const values =
      group === "activation"
        ? activationParams
        : group === "segmentEngine"
          ? segmentEngineParams
          : group === "commercialOpportunities"
            ? commercialOpportunityParams
            : group === "rewardsEngine"
              ? rewardsEngineParams
            : group === "productMix"
              ? productMixParams
              : group === "dailyControl"
                ? dailyControlParams
                : group === "productAnalysis"
                  ? productAnalysisParams
                  : promotionParams;
    const def = definitions.find((p) => p.key === paramKey)?.default ?? "";
    setEditingParam({
      key: paramKey,
      value: values[paramKey] ?? def,
      descriptionKey,
      group,
    });
    setEditValue(values[paramKey] ?? def);
    setEditModalVisible(true);
  };

  const handleSaveOrganizationParam = async () => {
    if (!editingParam) return;
    if (
      (editingParam.key === "PROMOTION_DISCOUNT_VALUES" ||
        editingParam.key === "PROMOTION_DAYS_IN_ADVANCE" ||
        editingParam.key === "PROMOTION_MAX_DURATION_DAYS") &&
      editValue.trim() === ""
    ) {
      message.error(t("Common-RequiredField"));
      return;
    }
    try {
      await createOrUpdateOrganizationParameter(
        editingParam.key,
        editValue,
        t(editingParam.descriptionKey)
      );
      if (editingParam.group === "activation") {
        setActivationParams((prev) => ({
          ...prev,
          [editingParam.key]: editValue,
        }));
      } else if (editingParam.group === "segmentEngine") {
        setSegmentEngineParams((prev) => ({
          ...prev,
          [editingParam.key]: editValue,
        }));
      } else if (editingParam.group === "commercialOpportunities") {
        setCommercialOpportunityParams((prev) => ({
          ...prev,
          [editingParam.key]: editValue,
        }));
      } else if (editingParam.group === "rewardsEngine") {
        setRewardsEngineParams((prev) => ({
          ...prev,
          [editingParam.key]: editValue,
        }));
      } else if (editingParam.group === "productMix") {
        setProductMixParams((prev) => ({
          ...prev,
          [editingParam.key]: editValue,
        }));
      } else if (editingParam.group === "dailyControl") {
        setDailyControlParams((prev) => ({
          ...prev,
          [editingParam.key]: editValue,
        }));
      } else if (editingParam.group === "productAnalysis") {
        setProductAnalysisParams((prev) => ({
          ...prev,
          [editingParam.key]: editValue,
        }));
      } else {
        setPromotionParams((prev) => ({
          ...prev,
          [editingParam.key]: editValue,
        }));
      }
      setEditModalVisible(false);
      setEditingParam(null);
      message.success(t("Common-EditSuccessMessage"));
    } catch {
      message.error(t("Common-ErrorMessage"));
    }
  };

  const showDeleteConfirm = (
    itemId: number,
    entity: "season" | "commercialDate"
  ) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        if (entity === "season") handleDeleteSeason(itemId);
        else if (entity === "commercialDate")
          handleDeleteCommercialDate(itemId);
      },
    });
  };

  const commercialDatesColumns = [
    {
      title: t("Common-Name"),
      dataIndex: "name",
      size: 20,
    },
    {
      title: `${t("Common-StartDate")} ${currentYear}`,
      key: "currentYearStartingDate",
      size: 15,
      render: (text: any, record: any) => {
        const edition = currentYearEditionsByDate[record.id];
        return edition ? (
          moment(edition.startingDate, "YYYY-MM-DD").format("DD-MM")
        ) : (
          <Tag color="warning">{t("Common-NotLoadedYet")}</Tag>
        );
      },
    },
    {
      title: `${t("Common-EndDate")} ${currentYear}`,
      key: "currentYearEndingDate",
      size: 15,
      render: (text: any, record: any) => {
        const edition = currentYearEditionsByDate[record.id];
        return edition ? (
          moment(edition.endingDate, "YYYY-MM-DD").format("DD-MM")
        ) : (
          <Tag color="warning">{t("Common-NotLoadedYet")}</Tag>
        );
      },
    },
    {
      title: t("Common-Actions"),
      key: "action",
      size: 15,
      render: (text: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="edit"
                onClick={() => {
                  navigate(`/admin/parameters/commercialdates/${record.id}/view`);
                }}
                icon={<EditOutlined />}
              >
                {t("Common-Edit")}
              </Menu.Item>
              <Menu.Item
                key="delete"
                onClick={() => {
                  showDeleteConfirm(record.id, "commercialDate");
                }}
                icon={<DeleteOutlined />}
              >
                {t("Common-Delete")}
              </Menu.Item>
            </Menu>
          }
        >
          <Button className={styles.actionButtonMore}>
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];


  const seasonColumns = [
    {
      title: t("Common-Name"),
      dataIndex: "name",
      size: 20,
    },
    {
      title: t("Common-StartDate"),
      dataIndex: "startingDate",
      size: 15,
      render: (date: string) => moment(date, "YYYY-MM-DD").format("DD-MM"),
    },
    {
      title: t("Common-EndDate"),
      dataIndex: "endingDate",
      size: 15,
      render: (date: string) => moment(date, "YYYY-MM-DD").format("DD-MM"),
    },
    {
      title: t("Common-Actions"),
      key: "action",
      size: 15,
      render: (text: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="edit"
                onClick={() => {
                  navigate(`/admin/parameters/seasons/${record.id}/view`);
                }}
                icon={<EditOutlined />}
              >
                {t("Common-Edit")}
              </Menu.Item>
              <Menu.Item
                key="delete"
                onClick={() => {
                  showDeleteConfirm(record.id, "season");
                }}
                icon={<DeleteOutlined />}
              >
                {t("Common-Delete")}
              </Menu.Item>
            </Menu>
          }
        >
          <Button className={styles.actionButtonMore}>
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  const buildOrganizationParamColumns = (
    values: Record<string, string>,
    group:
      | "activation"
      | "segmentEngine"
      | "commercialOpportunities"
      | "rewardsEngine"
      | "productMix"
      | "dailyControl"
      | "productAnalysis"
      | "promotions"
  ) => [
    {
      title: t("Common-Parameter"),
      key: "description",
      render: (_: unknown, record: OrganizationParameterDefinition) =>
        t(record.descriptionKey),
    },
    {
      title: t("Common-Value"),
      dataIndex: "value",
      key: "value",
      render: (_: string, record: OrganizationParameterDefinition) =>
        values[record.key] ?? record.default,
    },
    {
      title: t("Common-Actions"),
      key: "action",
      width: 80,
      render: (_: unknown, record: OrganizationParameterDefinition) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() =>
            handleOpenEditParam(record.key, record.descriptionKey, group)
          }
        >
          {t("Common-Edit")}
        </Button>
      ),
    },
  ];

  const activationParamColumns = buildOrganizationParamColumns(
    activationParams,
    "activation"
  );

  const segmentEngineParamColumns = buildOrganizationParamColumns(
    segmentEngineParams,
    "segmentEngine"
  );

  const commercialOpportunityParamColumns = buildOrganizationParamColumns(
    commercialOpportunityParams,
    "commercialOpportunities"
  );

  const rewardsEngineParamColumns = buildOrganizationParamColumns(
    rewardsEngineParams,
    "rewardsEngine"
  );

  const productMixParamColumns = buildOrganizationParamColumns(
    productMixParams,
    "productMix"
  );

  const dailyControlParamColumns = buildOrganizationParamColumns(
    dailyControlParams,
    "dailyControl"
  );

  const productAnalysisParamColumns = buildOrganizationParamColumns(
    productAnalysisParams,
    "productAnalysis"
  );

  const promotionParamColumns = buildOrganizationParamColumns(
    promotionParams,
    "promotions"
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("Common-Parameters")}</h1>
      </div>
      <div className={styles.contentWrapper}>
        <Collapse
          activeKey={activeCollapseKeys}
          onChange={(keys) =>
            setActiveCollapseKeys(
              typeof keys === "string" ? [keys] : (keys as string[])
            )
          }
          className={styles.parametersCollapse}
        >
          <Collapse.Panel
            header={
              <div className={styles.collapseHeader}>
                <span className={styles.collapseTitle}>
                  {t("Common-CommercialDates")}
                </span>
                <Button
                  type="primary"
                  className={styles.newButton}
                  icon={<PlusOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/admin/parameters/commercialdates/create");
                  }}
                />
              </div>
            }
            key="commercialDates"
          >
            <div className={styles.tableWrapper}>
              <Table
                size="small"
                className={styles.table}
                rowKey={(record) => record.id}
                loading={loadingDates}
                columns={commercialDatesColumns}
                dataSource={commercialDates}
                pagination={{
                  total: totalSizeDates,
                  pageSize: 10,
                  showSizeChanger: false,
                  onChange: handlePaginationComercialDates,
                  current: actualPageDates,
                }}
              />
            </div>
          </Collapse.Panel>
          <Collapse.Panel
            header={
              <div className={styles.collapseHeader}>
                <span className={styles.collapseTitle}>
                  {t("Common-Seasons")}
                </span>
                <Button
                  type="primary"
                  className={styles.newButton}
                  icon={<PlusOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/admin/parameters/seasons/create");
                  }}
                />
              </div>
            }
            key="seasons"
          >
            <div className={styles.tableWrapper}>
              <Table
                size="small"
                className={styles.table}
                rowKey={(record) => record.id}
                loading={loadingSeasons}
                columns={seasonColumns}
                dataSource={seasons}
                pagination={{
                  total: totalSizeSeasons,
                  pageSize: 10,
                  showSizeChanger: false,
                  onChange: handlePaginationSeasons,
                  current: actualPageSeasons,
                }}
              />
            </div>
          </Collapse.Panel>
          <Collapse.Panel
            header={
              <span className={styles.collapseTitle}>
                {t("ActivationParam-SectionTitle")}
              </span>
            }
            key="activationParams"
          >
            <div className={styles.tableWrapper}>
              <Table
                size="small"
                className={styles.table}
                rowKey="key"
                loading={loadingActivationParams}
                columns={activationParamColumns}
                dataSource={ACTIVATION_PARAM_KEYS}
                pagination={false}
              />
            </div>
          </Collapse.Panel>
          <Collapse.Panel
            header={
              <span className={styles.collapseTitle}>
                {t("SegmentEngineParam-SectionTitle")}
              </span>
            }
            key="segmentEngineParams"
          >
            <div className={styles.tableWrapper}>
              <Table
                size="small"
                className={styles.table}
                rowKey="key"
                loading={loadingSegmentEngineParams}
                columns={segmentEngineParamColumns}
                dataSource={SEGMENT_ENGINE_PARAM_KEYS}
                pagination={false}
              />
            </div>
          </Collapse.Panel>
          <Collapse.Panel
            header={
              <span className={styles.collapseTitle}>
                {t("RewardsEngineParam-SectionTitle")}
              </span>
            }
            key="rewardsEngineParams"
          >
            <div className={styles.tableWrapper}>
              <Table
                size="small"
                className={styles.table}
                rowKey="key"
                loading={loadingRewardsEngineParams}
                columns={rewardsEngineParamColumns}
                dataSource={REWARDS_ENGINE_PARAM_KEYS}
                pagination={false}
              />
            </div>
            <RewardsTicketBucketsEditor />
          </Collapse.Panel>
          <Collapse.Panel
            header={
              <span className={styles.collapseTitle}>
                {t("CommercialOpportunityParam-SectionTitle")}
              </span>
            }
            key="commercialOpportunityParams"
          >
            <div className={styles.tableWrapper}>
              <Table
                size="small"
                className={styles.table}
                rowKey="key"
                loading={loadingCommercialOpportunityParams}
                columns={commercialOpportunityParamColumns}
                dataSource={COMMERCIAL_OPPORTUNITY_PARAM_KEYS}
                pagination={false}
              />
            </div>
          </Collapse.Panel>
          <Collapse.Panel
            header={
              <span className={styles.collapseTitle}>
                {t("ProductMixParam-SectionTitle")}
              </span>
            }
            key="productMixParams"
          >
            <div className={styles.tableWrapper}>
              <Table
                size="small"
                className={styles.table}
                rowKey="key"
                loading={loadingProductMixParams}
                columns={productMixParamColumns}
                dataSource={PRODUCT_MIX_PARAM_KEYS}
                pagination={false}
              />
            </div>
          </Collapse.Panel>
          <Collapse.Panel
            header={
              <span className={styles.collapseTitle}>
                {t("DailyControlParam-SectionTitle")}
              </span>
            }
            key="dailyControlParams"
          >
            <div className={styles.tableWrapper}>
              <Table
                size="small"
                className={styles.table}
                rowKey="key"
                loading={loadingDailyControlParams}
                columns={dailyControlParamColumns}
                dataSource={DAILY_CONTROL_PARAM_KEYS}
                pagination={false}
              />
            </div>
          </Collapse.Panel>
          <Collapse.Panel
            header={
              <span className={styles.collapseTitle}>
                {t("ProductAnalysisParam-SectionTitle")}
              </span>
            }
            key="productAnalysisParams"
          >
            <div className={styles.tableWrapper}>
              <Table
                size="small"
                className={styles.table}
                rowKey="key"
                loading={loadingProductAnalysisParams}
                columns={productAnalysisParamColumns}
                dataSource={PRODUCT_ANALYSIS_PARAM_KEYS}
                pagination={false}
              />
            </div>
          </Collapse.Panel>
          <Collapse.Panel
            header={
              <span className={styles.collapseTitle}>
                {t("PromotionParam-SectionTitle")}
              </span>
            }
            key="promotionParams"
          >
            <div className={styles.tableWrapper}>
              <Table
                size="small"
                className={styles.table}
                rowKey="key"
                loading={loadingPromotionParams}
                columns={promotionParamColumns}
                dataSource={PROMOTION_PARAM_KEYS}
                pagination={false}
              />
            </div>
          </Collapse.Panel>
        </Collapse>
      </div>
      <Modal
        title={t("Common-Edit")}
        visible={editModalVisible}
        onOk={handleSaveOrganizationParam}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingParam(null);
        }}
        okText={t("Common-Save")}
        cancelText={t("Common-Cancel")}
      >
        {editingParam && (
          <div>
            <p style={{ marginBottom: 8 }}>
              {t(editingParam.descriptionKey)}
            </p>
            {editingParam.key === "CHILD_CATEGORY_IDS" ? (
              <Select
                mode="multiple"
                allowClear
                style={{ width: "100%" }}
                placeholder={t("Common-Value")}
                optionFilterProp="label"
                value={editValue
                  .split(",")
                  .map((token) => token.trim())
                  .filter((token) => token.length > 0)}
                onChange={(values: string[]) =>
                  setEditValue(values.join(","))
                }
                options={categories.map((category) => ({
                  label: category.name,
                  value: String(category.id),
                }))}
              />
            ) : editingParam.key === "PROMOTION_DISCOUNT_VALUES" ? (
              <Select
                mode="tags"
                style={{ width: "100%" }}
                tokenSeparators={[","]}
                placeholder={t("PromotionParam-DiscountValuesPlaceholder")}
                value={editValue
                  .split(",")
                  .map((token) => token.trim())
                  .filter((token) => token.length > 0)}
                onChange={(values: string[]) =>
                  setEditValue(
                    values
                      .map((token) => token.trim())
                      .filter((token) => /^[1-9]\d*$/.test(token))
                      .join(",")
                  )
                }
              />
            ) : editingParam.key === "PROMOTION_DAYS_IN_ADVANCE" ||
              editingParam.key === "PROMOTION_MAX_DURATION_DAYS" ? (
              <InputNumber
                min={1}
                precision={0}
                style={{ width: "100%" }}
                value={editValue.trim() === "" ? null : Number(editValue)}
                onChange={(value) =>
                  setEditValue(value == null ? "" : String(value))
                }
              />
            ) : editingParam.key === "REUSE_PROMOTIONS" ? (
              <Select
                style={{ width: "100%" }}
                value={editValue}
                onChange={(value: string) => setEditValue(value)}
                options={[
                  { label: "false", value: "false" },
                  { label: "true", value: "true" },
                ]}
              />
            ) : (
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                placeholder={t("Common-Value")}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Parameters;
